import { useEffect, useRef, useState, FC, PropsWithChildren } from 'react'
import { useLocation } from 'react-router'
import styled from 'styled-components'

import useBodyScrollLock from 'hooks/useBodyScrollLock'
import { scrollableNodeStyles } from 'styles/bodyScrollLock'

import InvisibleButton from '../../../components/display/InvisibleButton'
import CloseIcon from '../../../components/icons/AirbnbClose'
import MenuBurger from '../../../components/icons/MenuBurger'

import { mobileMenuZIndex } from '../constants'
import { InverseOption } from '../types'
import { IncompleteBookingIndicator } from '../IncompleteBooking'

const Trigger = styled(InvisibleButton)<InverseOption>`
  display: flex;
  outline: none;
  position: relative;

  svg {
    flex-shrink: 0;
    color: ${({ inverse, theme }) =>
      inverse ? theme['-ds-neutral-100'].value : theme['-ds-core-400'].value};
    width: 36px;
    margin: 0;
  }
`

const CloseButton = styled(InvisibleButton)`
  padding: 24px;
  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme['-ds-core-300'].value};
  }
`

const Panel = styled.div<{ open: boolean }>`
  transition: opacity 150ms linear;

  opacity: ${({ open }) => (open ? '1' : '0')};
  visibility: ${({ open }) => (open === true ? 'visible' : 'hidden')};

  z-index: ${mobileMenuZIndex};

  background: ${({ theme }) => theme['-ds-neutral-100'].value};
  padding: 66px 21px 21px 21px;

  position: fixed;
  right: 0;
  left: 0;
  bottom: 0;
  top: 0;

  ${CloseButton} {
    position: absolute;
    top: 0;
    right: 0;
  }

  ${scrollableNodeStyles}
`

const Container = styled.div`
  display: flex;
  position: relative;
`

type MenuProps = InverseOption & {
  hasIncompleteBooking: Boolean
}

const Menu: FC<PropsWithChildren<MenuProps>> = ({
  inverse,
  children,
  hasIncompleteBooking = false,
}) => {
  const [open, setOpen] = useState(false)

  const panelRef = useRef<HTMLDivElement>()

  const location = useLocation()

  useEffect(() => {
    setOpen(false)
  }, [location])

  const {
    enableBodyScroll,
    disableBodyScroll,
    clearAllBodyScrollLocks,
  } = useBodyScrollLock()

  useEffect(() => {
    if (open) {
      disableBodyScroll(panelRef.current)
    } else {
      enableBodyScroll(panelRef.current)
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [open, panelRef])

  const toggleOpen = () => setOpen(!open)

  return (
    <Container>
      <Trigger
        onClick={toggleOpen}
        inverse={inverse}
        aria-label="Open navigation menu"
      >
        <MenuBurger />
        {hasIncompleteBooking && <IncompleteBookingIndicator />}
      </Trigger>

      <Panel ref={panelRef} open={open}>
        <CloseButton
          onClick={() => setOpen(false)}
          aria-label="Close navigation menu"
        >
          <CloseIcon />
        </CloseButton>

        {children}
      </Panel>
    </Container>
  )
}

export default Menu
